/**
 * The language every Synstream site shares.
 *
 * Each site used to keep its choice in its own localStorage, which no other host
 * can read, so `?lang` on the outbound link was the only way to carry it across.
 * That works, but only one way and only once: a link built here hands the
 * language over, a bookmark or a typed address does not, and a language changed
 * here afterwards never reaches a dashboard the person has already opened.
 *
 * A cookie on the parent domain is read and written by every site beneath it --
 * the same mechanism the session cookie already relies on, which the auth
 * service scopes with COOKIE_DOMAIN=.uat.synstream.co. One choice then follows
 * the person across the console and every dashboard, and a later change
 * propagates instead of leaving each site on its own stale copy.
 *
 * `?lang` stays as the fallback, because a cookie cannot span two registrable
 * domains. Which dashboard a console links to differs per deployment, and some
 * of them sit outside its own domain entirely -- dashboard.nonprod.rtsinv.com
 * reached from console.uat.synstream.co shares no parent to put a cookie on.
 * See lib/dashboardLink.ts.
 *
 * The dashboard repo carries a matching copy of this file. The two apps are
 * separate builds with no shared package, so the cookie name, lifetime and
 * domain rule have to agree by hand.
 */

export const LANGUAGE_COOKIE = "synstream_lang";

/** A year, in minutes. The choice is a preference, not a session. */
export const LANGUAGE_COOKIE_MINUTES = 525600;

/**
 * The domain to scope the cookie to, or undefined for a host-only cookie.
 *
 * `console.uat.synstream.co` yields `.uat.synstream.co`, which every sibling
 * site reads. Under three labels the parent would be a public suffix and the
 * browser refuses the cookie outright; localhost and bare IP addresses take no
 * domain attribute at all. In each of those cases a host-only cookie is still
 * useful -- it just does not travel, which is the correct outcome when there is
 * no shared parent to travel across.
 */
export function languageCookieDomain(
  hostname: string = typeof window === "undefined" ? "" : window.location.hostname,
): string | undefined {
  if (!hostname || hostname === "localhost") return undefined;
  // IPv4 literal, or an IPv6 literal as the address bar writes it.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname) || hostname.startsWith("[")) return undefined;
  const labels = hostname.split(".");
  if (labels.length < 3) return undefined;
  return `.${labels.slice(1).join(".")}`;
}

/** Cookie attributes for the language detector's cache. */
export function languageCookieOptions() {
  return {
    path: "/",
    // Lax, not Strict: the same value the session cookie uses, and it survives
    // the console -> dashboard navigation on any future host arrangement.
    sameSite: "lax" as const,
    secure: typeof window !== "undefined" && window.location.protocol === "https:",
  };
}
